import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  BarChart, Bar, PieChart, Pie, Cell, LineChart, Line,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts';
import {
  ArrowLeft, RefreshCw, Calendar, ChevronDown,
  Brain, Shield, AlertTriangle, FileText, TrendingUp,
  Target, Crosshair,
} from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const RISK_COLORS = { high: '#ef4444', medium: '#f59e0b', low: '#22c55e', unknown: '#64748b' };
const CHART_COLORS = [
  '#3b82f6', '#8b5cf6', '#f59e0b', '#ef4444', '#10b981',
  '#ec4899', '#06b6d4', '#f97316', '#6366f1', '#14b8a6',
];

export default function PatternIntelligencePage({ user }) {
  const navigate = useNavigate();
  const [days, setDays] = useState(180);
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };
      const res = await axios.get(
        `${API}/api/pattern-intelligence/dashboard?days=${days}`,
        { headers }
      );
      setData(res.data);
    } catch (err) {
      console.error('Failed to fetch pattern intelligence:', err);
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const riskPie = (data?.risk_distribution || []).map(r => ({
    name: r.level, value: r.count,
  }));

  return (
    <div className="pi-dash" data-testid="pattern-intelligence-dashboard">
      <style>{piStyles}</style>

      {/* Header */}
      <div className="pi-header">
        <div className="pi-header-left">
          <button className="pi-back" onClick={() => navigate('/dashboard')} data-testid="pi-back-btn">
            <ArrowLeft size={16} /> Back
          </button>
          <div>
            <h1 className="pi-title">Union Brain</h1>
            <p className="pi-subtitle">Pattern Intelligence from Fact Finder usage</p>
          </div>
        </div>
        <div className="pi-header-right">
          <div className="pi-range-select">
            <Calendar size={14} />
            <select value={days} onChange={e => setDays(+e.target.value)}>
              <option value={30}>Last 30 days</option>
              <option value={90}>Last 90 days</option>
              <option value={180}>Last 6 months</option>
              <option value={365}>Last year</option>
            </select>
            <ChevronDown size={12} />
          </div>
          <button className="pi-refresh" onClick={fetchData} disabled={loading} data-testid="pi-refresh-btn">
            <RefreshCw size={14} className={loading ? 'pi-spin' : ''} /> Refresh
          </button>
        </div>
      </div>

      {loading ? (
        <div className="pi-loading">
          <RefreshCw size={28} className="pi-spin" />
          <span>Loading pattern intelligence...</span>
        </div>
      ) : !data || data.total_records === 0 ? (
        <div className="pi-empty-state" data-testid="pi-empty">
          <Brain size={48} />
          <h3>No Pattern Data Yet</h3>
          <p>Pattern data is collected automatically when workers use the Fact Finder. Run a few analyses to start seeing trends.</p>
        </div>
      ) : (
        <>
          {/* Stat Cards */}
          <div className="pi-stats-grid">
            <StatCard icon={Brain} label="Cases Analyzed" value={data.total_records} color="#3b82f6" />
            <StatCard
              icon={AlertTriangle}
              label="High Risk"
              value={riskPie.find(r => r.name === 'high')?.value || 0}
              sub={`of ${data.total_records} total`}
              color="#ef4444"
            />
            <StatCard icon={Target} label="Unique Tactics" value={data.top_tactics?.length || 0} color="#f59e0b" />
            <StatCard icon={FileText} label="Contract Refs" value={data.top_articles?.length || 0} color="#8b5cf6" />
          </div>

          {/* Row 1: Investigation Types + Risk Distribution */}
          <div className="pi-grid-2" data-testid="pi-reports">
            {/* Report 1: Most Common Investigation Types */}
            <div className="pi-card" data-testid="pi-incident-types">
              <h3 className="pi-card-title">
                <Crosshair size={16} style={{ color: '#3b82f6' }} />
                Most Common Investigation Types
              </h3>
              {data.top_incident_types?.length > 0 ? (
                <ResponsiveContainer width="100%" height={240}>
                  <BarChart data={data.top_incident_types} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
                    <XAxis type="number" tick={{ fill: '#64748b', fontSize: 12 }} />
                    <YAxis dataKey="tag" type="category" tick={{ fill: '#94a3b8', fontSize: 12 }} width={160} />
                    <Tooltip contentStyle={{ background: '#1e293b', border: '1px solid #334155', borderRadius: 8, color: '#e2e8f0' }} />
                    <Bar dataKey="count" fill="#3b82f6" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="pi-empty-card">No data yet</div>
              )}
            </div>

            {/* Risk Distribution Pie */}
            <div className="pi-card" data-testid="pi-risk-chart">
              <h3 className="pi-card-title">
                <Shield size={16} style={{ color: '#ef4444' }} />
                Risk Distribution
              </h3>
              {riskPie.length > 0 ? (
                <div className="pi-pie-wrap">
                  <ResponsiveContainer width="100%" height={240}>
                    <PieChart>
                      <Pie data={riskPie} cx="50%" cy="50%" innerRadius={55} outerRadius={90} dataKey="value" paddingAngle={3}>
                        {riskPie.map((entry, i) => (
                          <Cell key={i} fill={RISK_COLORS[entry.name] || '#64748b'} />
                        ))}
                      </Pie>
                      <Tooltip contentStyle={{ background: '#1e293b', border: '1px solid #334155', borderRadius: 8, color: '#e2e8f0' }} />
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="pi-pie-legend">
                    {riskPie.map((r, i) => (
                      <div key={i} className="pi-legend-item">
                        <span className="pi-legend-dot" style={{ background: RISK_COLORS[r.name] || '#64748b' }} />
                        <span className="pi-legend-label">{r.name}</span>
                        <span className="pi-legend-count">{r.value}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="pi-empty-card">No data yet</div>
              )}
            </div>
          </div>

          {/* Row 2: Top Management Tactics + Top Contract Articles */}
          <div className="pi-grid-2" style={{ marginTop: 16 }}>
            {/* Report 2: Most Common Management Tactics */}
            <div className="pi-card" data-testid="pi-tactics">
              <h3 className="pi-card-title">
                <Target size={16} style={{ color: '#f59e0b' }} />
                Most Common Management Tactics
              </h3>
              {data.top_tactics?.length > 0 ? (
                <div className="pi-tag-list">
                  {data.top_tactics.slice(0, 10).map((t, i) => (
                    <div key={i} className="pi-tag-row" data-testid={`tactic-tag-${i}`}>
                      <span className="pi-tag-rank">{i + 1}</span>
                      <span className="pi-tag-name">{t.tag}</span>
                      <div className="pi-tag-bar-wrap">
                        <div
                          className="pi-tag-bar"
                          style={{
                            width: `${(t.count / (data.top_tactics[0]?.count || 1)) * 100}%`,
                            background: CHART_COLORS[i % CHART_COLORS.length],
                          }}
                        />
                      </div>
                      <span className="pi-tag-count">{t.count}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="pi-empty-card">No tactics detected yet</div>
              )}
            </div>

            {/* Report 3: Most Referenced Contract/Safety Sections */}
            <div className="pi-card" data-testid="pi-articles">
              <h3 className="pi-card-title">
                <FileText size={16} style={{ color: '#8b5cf6' }} />
                Most Referenced Contract Sections
              </h3>
              {data.top_articles?.length > 0 ? (
                <div className="pi-tag-list">
                  {data.top_articles.slice(0, 10).map((t, i) => (
                    <div key={i} className="pi-tag-row" data-testid={`article-tag-${i}`}>
                      <span className="pi-tag-rank">{i + 1}</span>
                      <span className="pi-tag-name">{t.tag}</span>
                      <div className="pi-tag-bar-wrap">
                        <div
                          className="pi-tag-bar"
                          style={{
                            width: `${(t.count / (data.top_articles[0]?.count || 1)) * 100}%`,
                            background: CHART_COLORS[(i + 3) % CHART_COLORS.length],
                          }}
                        />
                      </div>
                      <span className="pi-tag-count">{t.count}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="pi-empty-card">No contract references yet</div>
              )}
            </div>
          </div>

          {/* Row 3: Top Issue Tags + Department Breakdown */}
          <div className="pi-grid-2" style={{ marginTop: 16 }}>
            {/* Report 4: Top Risk Patterns / Issue Tags */}
            <div className="pi-card" data-testid="pi-issues">
              <h3 className="pi-card-title">
                <AlertTriangle size={16} style={{ color: '#ef4444' }} />
                Top Risk Patterns (Issue Tags)
              </h3>
              {data.top_issues?.length > 0 ? (
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart data={data.top_issues.slice(0, 8)}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
                    <XAxis dataKey="tag" tick={{ fill: '#94a3b8', fontSize: 10 }} angle={-30} textAnchor="end" height={70} />
                    <YAxis tick={{ fill: '#64748b', fontSize: 12 }} />
                    <Tooltip contentStyle={{ background: '#1e293b', border: '1px solid #334155', borderRadius: 8, color: '#e2e8f0' }} />
                    <Bar dataKey="count" radius={[4, 4, 0, 0]}>
                      {data.top_issues.slice(0, 8).map((_, i) => (
                        <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="pi-empty-card">No issue data yet</div>
              )}
            </div>

            {/* Department Breakdown */}
            <div className="pi-card" data-testid="pi-departments">
              <h3 className="pi-card-title">
                <TrendingUp size={16} style={{ color: '#10b981' }} />
                Department Breakdown
              </h3>
              {data.top_departments?.length > 0 ? (
                <div className="pi-tag-list">
                  {data.top_departments.map((d, i) => (
                    <div key={i} className="pi-tag-row" data-testid={`dept-${i}`}>
                      <span className="pi-tag-rank">{i + 1}</span>
                      <span className="pi-tag-name">{d.department}</span>
                      <div className="pi-tag-bar-wrap">
                        <div
                          className="pi-tag-bar"
                          style={{
                            width: `${(d.count / (data.top_departments[0]?.count || 1)) * 100}%`,
                            background: CHART_COLORS[(i + 6) % CHART_COLORS.length],
                          }}
                        />
                      </div>
                      <span className="pi-tag-count">{d.count}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="pi-empty-card">No department data yet</div>
              )}
            </div>
          </div>

          {/* Row 4: Monthly Trend */}
          {data.monthly_trend?.length > 0 && (
            <div className="pi-card pi-card-wide" style={{ marginTop: 16 }} data-testid="pi-trend">
              <h3 className="pi-card-title">
                <TrendingUp size={16} style={{ color: '#3b82f6' }} />
                Monthly Activity Trend
              </h3>
              <ResponsiveContainer width="100%" height={240}>
                <LineChart data={data.monthly_trend}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
                  <XAxis dataKey="month" tick={{ fill: '#94a3b8', fontSize: 12 }} />
                  <YAxis tick={{ fill: '#64748b', fontSize: 12 }} />
                  <Tooltip contentStyle={{ background: '#1e293b', border: '1px solid #334155', borderRadius: 8, color: '#e2e8f0' }} />
                  <Line type="monotone" dataKey="count" name="Total Cases" stroke="#3b82f6" strokeWidth={2} dot={{ fill: '#3b82f6', r: 4 }} />
                  <Line type="monotone" dataKey="high_risk" name="High Risk" stroke="#ef4444" strokeWidth={2} dot={{ fill: '#ef4444', r: 4 }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function StatCard({ icon: Icon, label, value, sub, color = '#3b82f6' }) {
  return (
    <div className="pi-stat-card" data-testid={`pi-stat-${label.toLowerCase().replace(/\s/g, '-')}`}>
      <div className="pi-stat-icon" style={{ backgroundColor: `${color}18`, color }}>
        <Icon size={22} />
      </div>
      <div className="pi-stat-info">
        <span className="pi-stat-value">{value}</span>
        <span className="pi-stat-label">{label}</span>
        {sub && <span className="pi-stat-sub">{sub}</span>}
      </div>
    </div>
  );
}

const piStyles = `
  .pi-dash {
    min-height: 100vh;
    background: #0b1120;
    color: #e2e8f0;
    padding: 24px 32px;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  }
  .pi-header {
    display: flex; align-items: center; justify-content: space-between;
    margin-bottom: 24px; flex-wrap: wrap; gap: 16px;
  }
  .pi-header-left { display: flex; align-items: center; gap: 12px; }
  .pi-header-right { display: flex; align-items: center; gap: 12px; }
  .pi-back {
    display: flex; align-items: center; gap: 6px;
    background: transparent; color: #94a3b8; border: 1px solid #334155;
    padding: 8px 14px; border-radius: 8px; cursor: pointer; font-size: 13px;
    transition: all 0.15s;
  }
  .pi-back:hover { color: #e2e8f0; border-color: #475569; }
  .pi-title { font-size: 22px; font-weight: 700; color: #f8fafc; margin: 0; }
  .pi-subtitle { font-size: 13px; color: #64748b; margin: 2px 0 0; }
  .pi-range-select {
    display: flex; align-items: center; gap: 6px;
    background: #111827; border: 1px solid #1e293b; border-radius: 8px;
    padding: 6px 12px; color: #94a3b8; font-size: 13px;
  }
  .pi-range-select select {
    background: transparent; color: #e2e8f0; border: none; font-size: 13px;
    cursor: pointer; outline: none; -webkit-appearance: none;
  }
  .pi-range-select select option { background: #1e293b; }
  .pi-refresh {
    display: flex; align-items: center; gap: 6px;
    background: #3b82f6; color: white; border: none; padding: 8px 16px;
    border-radius: 8px; cursor: pointer; font-size: 13px; font-weight: 500;
  }
  .pi-refresh:hover { background: #2563eb; }
  .pi-refresh:disabled { opacity: 0.6; }

  .pi-loading {
    display: flex; flex-direction: column; align-items: center; justify-content: center;
    min-height: 300px; color: #64748b; gap: 12px;
  }
  .pi-spin { animation: pi-spin-anim 1s linear infinite; }
  @keyframes pi-spin-anim { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }

  .pi-empty-state {
    display: flex; flex-direction: column; align-items: center; justify-content: center;
    min-height: 400px; color: #64748b; gap: 16px; text-align: center;
  }
  .pi-empty-state h3 { color: #94a3b8; margin: 0; font-size: 18px; }
  .pi-empty-state p { max-width: 400px; font-size: 14px; line-height: 1.6; }

  /* Stat Cards */
  .pi-stats-grid {
    display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 14px; margin-bottom: 20px;
  }
  .pi-stat-card {
    background: #111827; border: 1px solid #1e293b; border-radius: 12px;
    padding: 18px; display: flex; align-items: center; gap: 14px;
    transition: border-color 0.2s;
  }
  .pi-stat-card:hover { border-color: #334155; }
  .pi-stat-icon {
    width: 42px; height: 42px; border-radius: 10px;
    display: flex; align-items: center; justify-content: center; flex-shrink: 0;
  }
  .pi-stat-info { display: flex; flex-direction: column; }
  .pi-stat-value { font-size: 22px; font-weight: 700; color: #f8fafc; }
  .pi-stat-label { font-size: 11px; color: #64748b; text-transform: uppercase; letter-spacing: 0.5px; }
  .pi-stat-sub { font-size: 10px; color: #475569; }

  /* Grid */
  .pi-grid-2 { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; }
  .pi-card {
    background: #111827; border: 1px solid #1e293b; border-radius: 12px; padding: 20px;
  }
  .pi-card-wide { grid-column: 1 / -1; }
  .pi-card-title {
    font-size: 15px; font-weight: 600; color: #e2e8f0; margin: 0 0 16px;
    display: flex; align-items: center; gap: 8px;
  }
  .pi-empty-card { text-align: center; color: #475569; padding: 40px 20px; font-size: 13px; }

  /* Tag List */
  .pi-tag-list { display: flex; flex-direction: column; gap: 8px; }
  .pi-tag-row {
    display: flex; align-items: center; gap: 10px; padding: 6px 0;
  }
  .pi-tag-rank {
    width: 22px; height: 22px; border-radius: 6px;
    background: #1e293b; color: #64748b; font-size: 11px; font-weight: 700;
    display: flex; align-items: center; justify-content: center; flex-shrink: 0;
  }
  .pi-tag-name { font-size: 13px; color: #cbd5e1; flex: 1; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .pi-tag-bar-wrap {
    width: 80px; height: 6px; background: #1e293b; border-radius: 3px; flex-shrink: 0;
  }
  .pi-tag-bar { height: 100%; border-radius: 3px; transition: width 0.3s ease; }
  .pi-tag-count {
    font-size: 14px; font-weight: 700; color: #f8fafc; min-width: 28px; text-align: right;
  }

  /* Pie Legend */
  .pi-pie-wrap { display: flex; align-items: center; gap: 20px; }
  .pi-pie-legend { display: flex; flex-direction: column; gap: 8px; }
  .pi-legend-item { display: flex; align-items: center; gap: 8px; }
  .pi-legend-dot { width: 10px; height: 10px; border-radius: 50%; flex-shrink: 0; }
  .pi-legend-label { font-size: 13px; color: #94a3b8; text-transform: capitalize; }
  .pi-legend-count { font-size: 14px; font-weight: 700; color: #f8fafc; }

  @media (max-width: 768px) {
    .pi-dash { padding: 16px; }
    .pi-grid-2 { grid-template-columns: 1fr; }
    .pi-header { flex-direction: column; }
    .pi-stats-grid { grid-template-columns: 1fr 1fr; }
    .pi-pie-wrap { flex-direction: column; }
  }
`;
